import {t, useLanguage, interfaceLocale} from '../../i18n';
// src/components/memberships/PublicMembershipPlansSection.jsx
// LOOP Membership Subscriptions Phase 1.5B — public discovery + safe pre-payment CTA.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Crown,
  Eye,
  ShieldCheck,
  Sparkles,
  Ticket,
} from "lucide-react";
import { callCallableNativeSafe } from "../../utils/callableNativeSafe";

function money(value, currency = "USD") {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat(interfaceLocale(), {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch (_) {
    return `$${Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}`;
  }
}

function cycleLabel(value) {
  const cycle = String(value || "monthly").toLowerCase();
  if (cycle === "weekly") return t("semana");
  if (cycle === "quarterly") return t("trimestre");
  if (cycle === "yearly") return t("año");
  return t("mes");
}

function benefitLabel(plan = {}) {
  const type = String(plan.benefitType || plan.membershipType || "credits").toLowerCase();

  if (type === "unlimited") return t("Uso ilimitado");

  if (type === "discount") {
    const discount = Number(plan.discountPercent || 0);
    return discount > 0 ? t('{discount}% de descuento',{discount}) : "Descuento incluido";
  }

  const credits = Number(plan.includedCredits || 0);
  const unit = String(plan.creditUnit || "servicios").trim() || "servicios";
  return `${credits} ${unit}`;
}

function checkoutPathFor(businessId, planId) {
  return `/membership-checkout/${encodeURIComponent(businessId)}/${encodeURIComponent(planId)}`;
}

function loginPathFor(returnTo) {
  return `/login?from=${encodeURIComponent(returnTo)}`;
}

function authFailureText(error) {
  let details = "";
  try {
    details = JSON.stringify(error?.details || error?.customData || {});
  } catch (_) {}

  return [
    error?.code,
    error?.message,
    details,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isAppCheckFailure(error) {
  const raw = authFailureText(error);
  return (
    raw.includes("app check") ||
    raw.includes("app-check") ||
    raw.includes("appcheck") ||
    raw.includes("firebase-app-check") ||
    raw.includes("app-check-token-missing") ||
    raw.includes("app-check-token-unavailable") ||
    raw.includes("debug-token-not-registered")
  );
}

function isAuthFailure(error) {
  if (isAppCheckFailure(error)) return false;

  const raw = authFailureText(error);
  return (
    raw.includes("unauthenticated") ||
    raw.includes("auth-token") ||
    raw.includes("token expired") ||
    raw.includes("id token")
  );
}

function failedPlanReasons(checks = {}, { ignoreBusinessPublic = false } = {}) {
  const reasons = [];

  if (checks.businessAvailable === false) reasons.push("El negocio está desactivado.");
  if (!ignoreBusinessPublic && checks.businessPublic === false) reasons.push("El negocio todavía no está publicado.");
  if (checks.planActive === false) reasons.push("El plan está pausado.");
  if (checks.onlineSalesEnabled === false) reasons.push("La venta online está desactivada.");
  if (checks.monthlyBilling === false) reasons.push("La suscripción online requiere ciclo mensual.");
  if (checks.validPrice === false) reasons.push("El precio debe ser mayor que $0.");
  if (checks.supportedCurrency === false) reasons.push("La moneda debe ser USD en esta fase.");
  if (checks.usefulBenefit === false) reasons.push("El plan necesita un beneficio utilizable.");
  if (checks.validServiceScope === false) reasons.push("Selecciona al menos un servicio aplicable.");

  return reasons;
}

export default function PublicMembershipPlansSection({
  businessId,
  businessName = "",
  user = null,
}) {
  useLanguage();
  const navigate = useNavigate();
  const [retryCount, setRetryCount] = useState(0);
  const [plans, setPlans] = useState([]);
  const [paymentsReady, setPaymentsReady] = useState(false);
  const [viewerCanManage, setViewerCanManage] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [businessPublic, setBusinessPublic] = useState(true);
  const [excludedPlans, setExcludedPlans] = useState([]);
  const [loading, setLoading] = useState(Boolean(businessId));
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const plansRailRef = useRef(null);

  const viewerUid = String(user?.uid || "").trim();
  const signedInWithUid = Boolean(viewerUid);

  useEffect(() => {
    let cancelled = false;

    async function loadPlans() {
      if (!businessId) {
        setPlans([]);
        setPaymentsReady(false);
        setViewerCanManage(false);
        setPreviewMode(false);
        setBusinessPublic(true);
        setExcludedPlans([]);
        setLoadError("");
        setLoading(false);
        setLoaded(true);
        return;
      }

      setLoading(true);
      setLoadError("");

      try {
        const callDiscovery = (requireAuth) => callCallableNativeSafe({
          functionName: "getPublicBusinessMembershipPlans",
          data: { businessId },
          requireAuth,
          requireAppCheck: true,
          timeoutMs: 45000,
        });

        let result = null;

        if (signedInWithUid) {
          try {
            // Important: the R2 direct public transport intentionally omits
            // Authorization. An authenticated call is required so the backend
            // can recognize owner/admin/platform preview permissions.
            result = await callDiscovery(true);
          } catch (authError) {
            // A stale local session must not break a genuinely public page.
            // Fall back to anonymous discovery only for authentication errors.
            if (!isAuthFailure(authError)) throw authError;
            result = await callDiscovery(false);
          }
        } else {
          result = await callDiscovery(false);
        }

        if (cancelled) return;

        const payload = result?.data || {};
        setPlans(Array.isArray(payload.plans) ? payload.plans : []);
        setPaymentsReady(payload.paymentsReady === true);
        setViewerCanManage(payload.viewerCanManage === true);
        setPreviewMode(payload.previewMode === true);
        setBusinessPublic(payload.businessPublic !== false);
        setExcludedPlans(
          Array.isArray(payload?.diagnostics?.excludedPlans)
            ? payload.diagnostics.excludedPlans
            : [],
        );
      } catch (error) {
        // Public business pages must remain usable even if membership discovery is unavailable.
        try {
          console.warn("[PublicMembershipPlansSection] membership plans unavailable", error);
        } catch (_) {}

        if (!cancelled) {
          setPlans([]);
          setPaymentsReady(false);
          setViewerCanManage(false);
          setPreviewMode(false);
          setBusinessPublic(true);
          setExcludedPlans([]);
          setLoadError(
            isAppCheckFailure(error)
              ? "No pudimos validar este dispositivo para cargar las membresías. Reabre Loop e inténtalo nuevamente."
              : "No pudimos cargar las membresías en este momento.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoaded(true);
        }
      }
    }

    loadPlans();

    return () => {
      cancelled = true;
    };
  }, [businessId, viewerUid, signedInWithUid, retryCount]);

  const planCountLabel = useMemo(
    () => `${plans.length} plan${plans.length === 1 ? "" : "es"}`,
    [plans.length],
  );

  const scrollPlansRail = (direction = 1) => {
    const rail = plansRailRef.current;
    if (!rail) return;

    rail.scrollBy({
      left: direction * Math.max(280, Math.round(rail.clientWidth * 0.82)),
      behavior: "smooth",
    });
  };

  const selectPlan = (plan) => {
    const planId = String(plan?.planId || "").trim();
    const checkoutEligible = plan?.checkoutEligible === true;

    if (
      !businessId ||
      !planId ||
      previewMode ||
      !businessPublic ||
      !checkoutEligible
    ) return;

    const checkoutPath = checkoutPathFor(businessId, planId);

    if (!signedInWithUid) {
      navigate(loginPathFor(checkoutPath));
      return;
    }

    navigate(checkoutPath);
  };

  if (loadError) return <div className="bp-membership-retry" role="status"><span>{t("Membresías temporalmente no disponibles.")}</span><button type="button" onClick={() => setRetryCount(n => n + 1)}>{t("Reintentar")}</button></div>;
  if (loading || !loaded) return null;
  const hasDiagnostics = previewMode && viewerCanManage && excludedPlans.length > 0;
  if (plans.length === 0 && !hasDiagnostics && !loadError) return null;

  return (
    <section className="pmps-shell pmps-shell-v2" aria-label={t("Membresías del negocio")}>
      <style>{styles}</style>

      <div className="pmps-head pmps-head-v2">
        <div className="pmps-heading-v2">
          <span className="pmps-title-bar-v2" aria-hidden="true" />
          <div>
            <h3 className="pmps-title-v2">{t("Membresías")}</h3>
            <p>{t("Beneficios pensados para clientes frecuentes de")}{businessName || "este negocio"}.
            </p>
          </div>
        </div>

        {plans.length > 0 ? (
          <div className="pmps-head-actions-v3">
            <span className="pmps-count pmps-count-v2">{planCountLabel}</span>

            {plans.length > 1 ? (
              <div className="pmps-carousel-actions-v3" aria-label={t("Navegar membresías")}>
                <button type="button" aria-label={t("Membresías anteriores")} onClick={() => scrollPlansRail(-1)}>
                  <ChevronLeft size={17} strokeWidth={2.2} />
                </button>
                <button type="button" aria-label={t("Membresías siguientes")} onClick={() => scrollPlansRail(1)}>
                  <ChevronRight size={17} strokeWidth={2.2} />
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {loadError ? (
        <div className="pmps-safety">
          <AlertCircle size={17} />
          <span>
            <strong>{t("No pudimos cargar las membresías.")}</strong>
            {loadError}
          </span>
        </div>
      ) : null}

      {previewMode ? (
        <div className="pmps-preview">
          <Eye size={17} />
          <span>
            <strong>{t("Vista previa privada.")}</strong>{t("Tú puedes ver estas membresías porque administras el negocio. Los clientes no las verán hasta que el negocio vuelva a estar publicado.")}</span>
        </div>
      ) : null}

      {!paymentsReady && plans.some((plan) => plan?.checkoutEligible === true) ? (
        <div className="pmps-safety">
          <ShieldCheck size={17} />
          <span>
            <strong>{t("Compra online temporalmente no disponible.")}</strong>{t("Puedes revisar los planes mientras terminamos de habilitar el pago online para este negocio.")}</span>
        </div>
      ) : null}

      {plans.length > 0 ? (
        <div className="pmps-grid pmps-carousel-v3" ref={plansRailRef}>
          {plans.map((plan) => {
            const checkoutEligible = plan?.checkoutEligible === true;
            const managerCanTestCheckout =
              viewerCanManage &&
              signedInWithUid &&
              !previewMode &&
              businessPublic &&
              checkoutEligible;

            const checkoutDisabled =
              previewMode ||
              !businessPublic ||
              !checkoutEligible ||
              (!paymentsReady && !managerCanTestCheckout);

            return (
              <article key={plan.planId} className="pmps-card">
                <div className="pmps-card-top">
                  <span className="pmps-icon"><Crown size={20} /></span>

                  <div className="pmps-copy">
                    <div className="pmps-title-line">
                      <strong>{plan.name || t("Membresía")}</strong>
                      <em className={checkoutEligible ? "is-online" : "is-profile"}>
                        {checkoutEligible ? "Online" : t("En el negocio")}
                      </em>
                    </div>
                    <p>{plan.description || t("Beneficios recurrentes para tus próximas visitas.")}</p>
                  </div>
                </div>

                <div className="pmps-meta">
                  <span><Ticket size={14} /> {benefitLabel(plan)}</span>
                  <strong>{money(plan.price, plan.currency)} <small>/ {cycleLabel(plan.billingCycle)}</small></strong>
                </div>

                {!checkoutEligible ? (
                  <p className="pmps-offline-note">{t("Este plan está visible en el perfil, pero el checkout online no está habilitado.")}</p>
                ) : null}

                <button
                  type="button"
                  disabled={checkoutDisabled}
                  onClick={() => selectPlan(plan)}
                >
                  <span>
                    {previewMode || !businessPublic
                      ? t("Vista previa")
                      : !checkoutEligible
                        ? t("Disponible en el negocio")
                        : paymentsReady
                          ? signedInWithUid
                            ? t("Elegir membresía")
                            : t("Iniciar sesión para comprar")
                          : managerCanTestCheckout
                            ? t("Probar checkout")
                            : t("Próximamente")}
                  </span>
                  {checkoutDisabled ? null : <ChevronRight size={17} />}
                </button>
              </article>
            );
          })}
        </div>
      ) : null}

      {hasDiagnostics ? (
        <aside className="pmps-diagnostics">
          <div className="pmps-diagnostics-head">
            <AlertCircle size={17} />
            <div>
              <strong>
                {plans.length === 0
                  ? "Tus planes todavía necesitan ajustes antes de mostrarse"
                  : `${excludedPlans.length} plan${excludedPlans.length === 1 ? "" : "es"} necesita${excludedPlans.length === 1 ? "" : "n"} ajustes de configuración`}
              </strong>
              <span>{t("Esta información solo se muestra a administradores del negocio.")}</span>
            </div>
          </div>

          <div className="pmps-diagnostic-list">
            {excludedPlans.map((item) => {
              const reasons = failedPlanReasons(item.checks || {}, {
                ignoreBusinessPublic: previewMode,
              });

              return (
                <div key={item.planId || item.name} className="pmps-diagnostic-item">
                  <strong>{item.name || t("Membresía")}</strong>
                  <span>
                    {item.displayEligible === true && item.checkoutEligible === false
                      ? `Visible en el perfil. ${reasons.length ? reasons.join(" ") : "El checkout online no está habilitado."}`
                      : reasons.length
                        ? reasons.join(" ")
                        : "Revisa la configuración del plan."}
                  </span>
                </div>
              );
            })}
          </div>
        </aside>
      ) : null}
    </section>
  );

}

const styles = `
  .pmps-shell{
    margin:22px 0;
    border:1px solid rgba(207,225,222,.95);
    background:
      radial-gradient(circle at 0% 0%,rgba(0,103,95,.08),transparent 32%),
      linear-gradient(180deg,#ffffff,#fbfefd);
    border-radius:28px;
    padding:20px;
    box-shadow:0 18px 42px rgba(15,23,42,.055);
  }
  .pmps-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:18px;
  }
  .pmps-kicker{
    display:inline-flex;
    align-items:center;
    gap:6px;
    color:#00675f;
    font-size:10.5px;
    font-weight:950;
    letter-spacing:.13em;
    text-transform:uppercase;
  }
  .pmps-head h3{
    margin:6px 0 0;
    color:#0f172a;
    font-size:23px;
    line-height:1.08;
    letter-spacing:-.04em;
  }
  .pmps-head p{
    margin:7px 0 0;
    max-width:650px;
    color:#64748b;
    font-size:12.5px;
    line-height:1.5;
    font-weight:700;
  }
  .pmps-count{
    flex:0 0 auto;
    border:1px solid #cfe1de;
    background:#f0fdfa;
    color:#00675f;
    border-radius:999px;
    padding:7px 10px;
    font-size:10.5px;
    font-weight:950;
  }
  .pmps-safety{
    margin-top:14px;
    border:1px solid rgba(0,103,95,.16);
    background:#f0fdfa;
    color:#00675f;
    border-radius:17px;
    padding:11px 12px;
    display:flex;
    align-items:flex-start;
    gap:9px;
  }
  .pmps-safety>span{
    color:#42645f;
    font-size:11px;
    line-height:1.45;
    font-weight:720;
  }
  .pmps-safety strong{
    color:#00675f;
    margin-right:4px;
  }
  .pmps-preview{
    margin-top:14px;
    border:1px solid rgba(2,132,199,.18);
    background:#f0f9ff;
    color:#0369a1;
    border-radius:17px;
    padding:11px 12px;
    display:flex;
    align-items:flex-start;
    gap:9px;
  }
  .pmps-preview>span{
    color:#475569;
    font-size:11px;
    line-height:1.45;
    font-weight:720;
  }
  .pmps-preview strong{
    color:#0369a1;
    margin-right:4px;
  }
  .pmps-grid{
    margin-top:16px;
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:12px;
  }
  .pmps-card{
    min-width:0;
    border:1px solid #e0e9ed;
    background:#fff;
    border-radius:21px;
    padding:14px;
    display:flex;
    flex-direction:column;
    box-shadow:0 10px 24px rgba(15,23,42,.04);
  }
  .pmps-card-top{
    display:flex;
    align-items:flex-start;
    gap:10px;
  }
  .pmps-icon{
    width:40px;
    height:40px;
    flex:0 0 40px;
    border-radius:14px;
    display:grid;
    place-items:center;
    background:#00675f;
    color:#fff;
  }
  .pmps-copy{
    min-width:0;
  }
  .pmps-copy strong{
    display:block;
    color:#0f172a;
    font-size:14px;
    line-height:1.25;
    font-weight:950;
  }
  .pmps-title-line{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:8px;
  }
  .pmps-title-line>strong{
    min-width:0;
  }
  .pmps-title-line em{
    flex:0 0 auto;
    border-radius:999px;
    padding:4px 7px;
    font-size:8.5px;
    line-height:1;
    font-style:normal;
    font-weight:950;
    letter-spacing:.03em;
  }
  .pmps-title-line em.is-online{
    background:#ecfdf5;
    color:#047857;
    border:1px solid #a7f3d0;
  }
  .pmps-title-line em.is-profile{
    background:#f8fafc;
    color:#64748b;
    border:1px solid #e2e8f0;
  }
  .pmps-copy p{
    margin:4px 0 0;
    color:#64748b;
    font-size:10.8px;
    line-height:1.45;
    font-weight:680;
  }
  .pmps-meta{
    margin:13px 0;
    display:flex;
    align-items:flex-end;
    justify-content:space-between;
    gap:10px;
    color:#00675f;
  }
  .pmps-meta>span{
    display:flex;
    align-items:center;
    gap:5px;
    font-size:10.5px;
    line-height:1.25;
    font-weight:900;
  }
  .pmps-meta>strong{
    color:#0f172a;
    font-size:14px;
    white-space:nowrap;
  }
  .pmps-meta small{
    color:#64748b;
    font-size:9.5px;
    font-weight:800;
  }
  .pmps-offline-note{
    margin:0 0 11px;
    border-radius:11px;
    background:#f8fafc;
    color:#64748b;
    padding:8px 9px;
    font-size:9.5px;
    line-height:1.35;
    font-weight:750;
  }
  .pmps-card button{
    width:100%;
    min-height:40px;
    margin-top:auto;
    border:0;
    border-radius:13px;
    background:#00675f;
    color:#fff;
    display:flex;
    align-items:center;
    justify-content:center;
    gap:7px;
    padding:0 12px;
    font-size:11px;
    font-weight:950;
    cursor:pointer;
  }
  .pmps-card button:hover:not(:disabled){
    filter:brightness(.97);
  }
  .pmps-card button:disabled{
    cursor:not-allowed;
    opacity:.58;
  }
  .pmps-diagnostics{
    margin-top:14px;
    border:1px solid #fed7aa;
    background:#fff7ed;
    border-radius:18px;
    padding:12px;
  }
  .pmps-diagnostics-head{
    display:flex;
    align-items:flex-start;
    gap:9px;
    color:#c2410c;
  }
  .pmps-diagnostics-head>div{
    min-width:0;
    display:grid;
    gap:2px;
  }
  .pmps-diagnostics-head strong{
    color:#9a3412;
    font-size:11.5px;
  }
  .pmps-diagnostics-head span{
    color:#7c2d12;
    font-size:10px;
    line-height:1.4;
    font-weight:700;
  }
  .pmps-diagnostic-list{
    margin-top:9px;
    display:grid;
    gap:7px;
  }
  .pmps-diagnostic-item{
    border:1px solid rgba(251,146,60,.28);
    background:rgba(255,255,255,.72);
    border-radius:12px;
    padding:9px 10px;
    display:grid;
    gap:3px;
  }
  .pmps-diagnostic-item strong{
    color:#7c2d12;
    font-size:10.8px;
  }
  .pmps-diagnostic-item span{
    color:#9a3412;
    font-size:10px;
    line-height:1.4;
    font-weight:700;
  }
  @media(max-width:900px){
    .pmps-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
  }
  @media(max-width:640px){
    .pmps-shell{
      margin:18px 0;
      padding:16px;
      border-radius:23px;
    }
    .pmps-head{
      display:grid;
      gap:10px;
    }
    .pmps-head h3{font-size:20px}
    .pmps-count{width:max-content}
    .pmps-grid{grid-template-columns:1fr}
  }

  /* =========================================================
     LOOP BUSINESSPAGE MEMBERSHIP INTEGRATION V2
     Memberships now behave like a native BusinessPage section:
     - stable in-place loading skeleton instead of late pop-in
     - same section rhythm / divider / heading language
     - no detached outer card around the entire module
     - responsive desktop/tablet/mobile contract
     ========================================================= */

  .pmps-shell-v2{
    width:100%!important;
    max-width:1320px!important;
    margin:34px auto 0!important;
    padding:34px 0 0!important;
    border:0!important;
    border-top:1px solid rgba(108,88,61,.11)!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
    box-sizing:border-box!important;
  }

  .pmps-head-v2{
    width:100%!important;
    display:flex!important;
    align-items:flex-end!important;
    justify-content:space-between!important;
    gap:18px!important;
    margin:0 0 18px!important;
  }

  .pmps-heading-v2{
    min-width:0!important;
    display:flex!important;
    align-items:flex-start!important;
    gap:14px!important;
  }

  .pmps-title-bar-v2{
    width:5px!important;
    min-width:5px!important;
    height:34px!important;
    margin-top:3px!important;
    border-radius:999px!important;
    background:linear-gradient(180deg,#00a693 0%,#00675f 100%)!important;
    box-shadow:0 8px 18px rgba(0,103,95,.16)!important;
  }

  .pmps-title-v2{
    margin:0!important;
    color:#10323d!important;
    font-size:clamp(26px,2.3vw,34px)!important;
    line-height:.98!important;
    letter-spacing:-.055em!important;
    font-weight:950!important;
  }

  .pmps-heading-v2 p{
    margin:7px 0 0!important;
    max-width:650px!important;
    color:#64748b!important;
    font-size:13px!important;
    line-height:1.42!important;
    font-weight:700!important;
  }

  .pmps-count-v2{
    flex:0 0 auto!important;
    min-height:34px!important;
    display:inline-flex!important;
    align-items:center!important;
    border:1px solid rgba(0,103,95,.14)!important;
    background:#f0fdfa!important;
    color:#00675f!important;
    border-radius:999px!important;
    padding:0 12px!important;
    font-size:11.5px!important;
    line-height:1!important;
    font-weight:950!important;
    box-shadow:0 8px 20px rgba(54,41,25,.04)!important;
  }

  .pmps-shell-v2 .pmps-grid{
    margin-top:0!important;
    display:grid!important;
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    gap:14px!important;
  }

  .pmps-shell-v2 .pmps-card{
    min-width:0!important;
    border:1px solid rgba(108,88,61,.10)!important;
    background:rgba(255,255,255,.92)!important;
    border-radius:22px!important;
    padding:15px!important;
    box-shadow:0 12px 30px rgba(54,41,25,.055)!important;
    transition:transform 170ms ease,box-shadow 170ms ease,border-color 170ms ease!important;
  }

  .pmps-shell-v2 .pmps-icon{
    background:#eaf8f4!important;
    color:#00796f!important;
    border:1px solid rgba(0,103,95,.08)!important;
  }

  .pmps-shell-v2 .pmps-card button{
    min-height:42px!important;
    border-radius:14px!important;
    background:#00796f!important;
  }

  .pmps-shell-v2 .pmps-safety,
  .pmps-shell-v2 .pmps-preview,
  .pmps-shell-v2 .pmps-diagnostics{
    border-radius:17px!important;
    box-shadow:none!important;
  }

  @media(hover:hover) and (pointer:fine){
    .pmps-shell-v2 .pmps-card:hover{
      transform:translateY(-2px)!important;
      border-color:rgba(0,103,95,.18)!important;
      box-shadow:0 18px 38px rgba(54,41,25,.075)!important;
    }
  }

  /* Stable loading state: BusinessPage no longer renders without
     the section and then inserts Memberships later. */
  .pmps-shell-v2.is-loading{
    min-height:250px!important;
  }

  .pmps-skeleton-grid-v2{
    pointer-events:none!important;
  }

  .pmps-skeleton-card-v2{
    min-width:0!important;
    min-height:170px!important;
    padding:15px!important;
    border:1px solid rgba(108,88,61,.08)!important;
    border-radius:22px!important;
    background:rgba(255,255,255,.64)!important;
    display:flex!important;
    flex-direction:column!important;
    gap:14px!important;
    overflow:hidden!important;
  }

  .pmps-skeleton-top-v2{
    display:flex!important;
    align-items:flex-start!important;
    gap:10px!important;
  }

  .pmps-skeleton-icon-v2,
  .pmps-skeleton-line-v2,
  .pmps-skeleton-button-v2{
    display:block!important;
    position:relative!important;
    overflow:hidden!important;
    background:linear-gradient(
      90deg,
      rgba(226,232,240,.70) 0%,
      rgba(248,250,252,.96) 48%,
      rgba(226,232,240,.70) 100%
    )!important;
    background-size:220% 100%!important;
    animation:pmps-v2-shimmer 1.25s ease-in-out infinite!important;
  }

  .pmps-skeleton-icon-v2{
    width:40px!important;
    height:40px!important;
    min-width:40px!important;
    border-radius:14px!important;
  }

  .pmps-skeleton-copy-v2{
    flex:1 1 auto!important;
    display:grid!important;
    gap:8px!important;
    padding-top:3px!important;
  }

  .pmps-skeleton-line-v2{
    height:10px!important;
    border-radius:999px!important;
  }

  .pmps-skeleton-line-v2.is-title{width:58%!important}
  .pmps-skeleton-line-v2.is-copy{width:88%!important}
  .pmps-skeleton-line-v2.is-meta{width:46%!important;margin-top:3px!important}
  .pmps-skeleton-button-v2{
    width:100%!important;
    height:42px!important;
    margin-top:auto!important;
    border-radius:14px!important;
  }

  @keyframes pmps-v2-shimmer{
    0%{background-position:100% 0}
    100%{background-position:-100% 0}
  }

  @media(max-width:900px){
    .pmps-shell-v2{
      margin-top:28px!important;
      padding-top:28px!important;
    }

    .pmps-shell-v2 .pmps-grid{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
    }

    .pmps-skeleton-grid-v2 .pmps-skeleton-card-v2:nth-child(3){
      display:none!important;
    }
  }

  @media(max-width:640px){
    .pmps-shell-v2{
      margin:28px 0 0!important;
      padding:28px 0 0!important;
      border-radius:0!important;
    }

    .pmps-head-v2{
      display:flex!important;
      align-items:flex-start!important;
      gap:12px!important;
      margin-bottom:16px!important;
    }

    .pmps-heading-v2{
      gap:11px!important;
    }

    .pmps-title-bar-v2{
      width:4px!important;
      min-width:4px!important;
      height:30px!important;
      margin-top:2px!important;
    }

    .pmps-title-v2{
      font-size:29px!important;
      line-height:.96!important;
      letter-spacing:-.065em!important;
    }

    .pmps-heading-v2 p{
      margin-top:6px!important;
      font-size:12.5px!important;
      line-height:1.38!important;
    }

    .pmps-count-v2{
      min-height:32px!important;
      padding:0 10px!important;
      font-size:10.5px!important;
    }

    .pmps-shell-v2 .pmps-grid{
      grid-template-columns:1fr!important;
      gap:12px!important;
    }

    .pmps-shell-v2 .pmps-card{
      border-radius:20px!important;
      padding:14px!important;
    }

    .pmps-shell-v2.is-loading{
      min-height:230px!important;
    }

    .pmps-skeleton-grid-v2 .pmps-skeleton-card-v2{
      display:none!important;
    }

    .pmps-skeleton-grid-v2 .pmps-skeleton-card-v2:first-child{
      display:flex!important;
      min-height:160px!important;
    }
  }

  @media(prefers-reduced-motion:reduce){
    .pmps-skeleton-icon-v2,
    .pmps-skeleton-line-v2,
    .pmps-skeleton-button-v2{
      animation:none!important;
    }
  }


  /* =========================================================
     LOOP MEMBERSHIPS CAROUSEL V3
     BusinessPage-friendly horizontal discovery:
     desktop ~3 cards, tablet ~2, mobile 1 card + next-card peek.
     Checkout/data logic stays unchanged.
     ========================================================= */

  .pmps-head-actions-v3{
    flex:0 0 auto!important;
    display:flex!important;
    align-items:center!important;
    justify-content:flex-end!important;
    gap:10px!important;
  }

  .pmps-carousel-actions-v3{
    display:flex!important;
    align-items:center!important;
    gap:7px!important;
  }

  .pmps-carousel-actions-v3 button{
    width:36px!important;
    height:36px!important;
    min-width:36px!important;
    padding:0!important;
    border:1px solid rgba(108,88,61,.12)!important;
    border-radius:999px!important;
    background:#ffffff!important;
    color:#0f5f57!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    box-shadow:0 8px 20px rgba(54,41,25,.05)!important;
    cursor:pointer!important;
  }

  .pmps-shell-v2 .pmps-grid.pmps-carousel-v3{
    margin-top:0!important;
    display:grid!important;
    grid-template-columns:none!important;
    grid-auto-flow:column!important;
    grid-auto-columns:clamp(270px,30vw,340px)!important;
    gap:14px!important;
    overflow-x:auto!important;
    overflow-y:hidden!important;
    padding:2px 3px 14px!important;
    scroll-snap-type:x mandatory!important;
    scroll-padding-inline:3px!important;
    overscroll-behavior-inline:contain!important;
    -webkit-overflow-scrolling:touch!important;
    scrollbar-width:none!important;
  }

  .pmps-shell-v2 .pmps-grid.pmps-carousel-v3::-webkit-scrollbar{
    display:none!important;
  }

  .pmps-shell-v2 .pmps-grid.pmps-carousel-v3 > .pmps-card{
    min-width:0!important;
    height:100%!important;
    scroll-snap-align:start!important;
  }

  @media(max-width:900px){
    .pmps-shell-v2 .pmps-grid.pmps-carousel-v3{
      grid-template-columns:none!important;
      grid-auto-columns:minmax(270px,48vw)!important;
    }
  }

  @media(max-width:640px){
    .pmps-head-actions-v3{
      align-items:flex-start!important;
      gap:8px!important;
    }

    .pmps-carousel-actions-v3{
      display:none!important;
    }

    .pmps-shell-v2 .pmps-grid.pmps-carousel-v3{
      grid-template-columns:none!important;
      grid-auto-flow:column!important;
      grid-auto-columns:min(84vw,330px)!important;
      gap:12px!important;
      margin-right:-14px!important;
      padding-right:14px!important;
      scroll-padding-inline:0!important;
    }

    .pmps-shell-v2 .pmps-grid.pmps-carousel-v3 > .pmps-card{
      border-radius:20px!important;
      padding:14px!important;
    }
  }


`;
